/**
 * Сборка занятия режима «Основы».
 *
 * Пока открытых клавиш не хватает на настоящие слова, упражнение состоит из
 * коротких сочетаний: три буквы — это движение, а не текст, и запоминается
 * оно как движение. Как только слова частотного списка набираются открытыми
 * клавишами, режим переходит на них — долго печатать бессмысленные
 * последовательности новичку нельзя (ТЗ §6, правило под таблицей режимов).
 *
 * Сборка детерминирована по seed: то же занятие воспроизводится в тестах
 * посимвольно (решение AD-012).
 */

import type { MasteryUnit } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { getWords } from '@type2x/corpora';
import {
  buildChunk,
  chunkAlphabet,
  createRng,
  learningChunkLimit,
} from '@type2x/exercise-generator';
import { DEFAULT_LEARNING_CONFIG, reviewShare, type LearningConfig } from '@type2x/learning-engine';

import type {
  FundamentalsExercise,
  FundamentalsPlan,
  FundamentalsSnapshot,
  KeyGroup,
} from '../contracts/fundamentals.js';
import {
  CHUNK_LENGTH,
  FUNDAMENTALS_HINT_LEVEL,
  MIN_WORDS_TO_SWITCH,
} from '../contracts/fundamentals.js';
import { completedGroups, currentGroup, keyGroups, keyUnit, unlockedCharacters } from './groups.js';
import { lessonItemCount, typingLatencyMs } from './length.js';

/** Какая доля элементов отводится текущей группе, когда пошли слова. */
const NEW_KEYS_SHARE = 0.25;

/**
 * Сколько букв нужно, чтобы сочетание было движением.
 *
 * Группа из одной клавиши даёт «яяя» — это удержание клавиши, а не движение.
 * В такую группу добавляется последняя из уже открытых: движение между двумя
 * соседними клавишами тренируется, а одиночное нажатие — нет.
 */
const MIN_ALPHABET = 2;

/** Слова, которые набираются только открытыми клавишами. */
function wordsFor(
  snapshot: FundamentalsSnapshot,
  unlocked: ReadonlySet<string>,
): readonly string[] {
  return getWords(snapshot.language, {
    allowedCharacters: unlocked,
    minLength: 2,
    limit: 60,
  }).map((word) => word.text);
}

/**
 * Доля повторения по открытым клавишам.
 *
 * Формула общая для всех режимов, открывающих материал группами; здесь только
 * собираются оценки. Клавиша открыта лишь после того, как её набирали, так
 * что единиц без статистики тут быть не может.
 */
function keyReviewShare(snapshot: FundamentalsSnapshot, known: readonly string[]): number {
  if (known.length === 0) return 0;

  const scores = known
    .map((character) => snapshot.stats[toMasteryUnitId(`key:${character}`)]?.masteryScore)
    .filter((score): score is number => score !== undefined);

  return reviewShare(scores);
}

/** Средняя длина элемента в символах: от неё зависит, сколько их уместится. */
function averageLength(items: readonly string[], fallback: number): number {
  if (items.length === 0) return fallback;
  return items.reduce((total, item) => total + [...item].length, 0) / items.length;
}

/** Собирает упражнение из сочетаний и — когда это уже возможно — из слов. */
function buildExercise(
  snapshot: FundamentalsSnapshot,
  seed: string,
  group: KeyGroup | null,
  unlocked: readonly string[],
  config: LearningConfig,
): FundamentalsExercise {
  const rng = createRng(seed);
  const words = wordsFor(snapshot, new Set(unlocked));
  const usesWords = words.length >= MIN_WORDS_TO_SWITCH;

  const groupLetters = group?.characters ?? unlocked;
  const known = unlocked.filter((character) => !groupLetters.includes(character));

  // Занятие меряется временем, а не числом элементов: слово длиннее
  // трёхбуквенного сочетания, и одинаковое их число означало бы занятия
  // разной длины на одном и том же материале.
  const itemCount = lessonItemCount({
    completedGroups: completedGroups(snapshot).length,
    totalGroups: keyGroups(snapshot).length,
    itemLength: usesWords ? averageLength(words, CHUNK_LENGTH) : CHUNK_LENGTH,
    latencyMs: typingLatencyMs(snapshot, unlocked, config),
  });

  const learning = chunkAlphabet(groupLetters, known, MIN_ALPHABET);
  const items: string[] = [];

  // Новые клавиши остаются в занятии и после перехода на слова: слово
  // подбирается по алфавиту, а не по тому, что сейчас осваивается. Повторение
  // в этой поре берут на себя сами слова — они набираются всем открытым.
  // Сверх доли повторения работает предел разнообразия: набор из двух букв
  // даёт шесть разных сочетаний, и сорок штук из них — петля, а не занятие
  // (находка владельца). Остаток занимает повторение открытых клавиш; время
  // занятия при этом не меняется. Повторять нечем, пока открытых нет.
  const planned = usesWords
    ? Math.round(itemCount * NEW_KEYS_SHARE)
    : itemCount - Math.round(itemCount * keyReviewShare(snapshot, known));
  const chunkCount =
    known.length === 0 ? planned : Math.min(planned, learningChunkLimit(learning, CHUNK_LENGTH));

  for (let index = 0; index < chunkCount; index += 1) {
    items.push(buildChunk(rng, learning, CHUNK_LENGTH));
  }
  for (let index = items.length; index < itemCount; index += 1) {
    items.push(usesWords ? rng.pick(words) : buildChunk(rng, known, CHUNK_LENGTH));
  }

  // Повторение перемешано с новым, а не сложено в конец: занятие, у которого
  // вторая половина легче первой, читается как «уже всё».
  const shuffled = rng.shuffle(items);
  return { seed, items: shuffled, text: shuffled.join(' '), usesWords };
}

/**
 * Строит занятие по текущему состоянию навыка.
 *
 * @param snapshot состояние навыка;
 * @param seed зерно генератора: одно и то же занятие воспроизводимо;
 * @param config конфигурация обучения: из неё берётся оценка скорости
 *   набора для того, кто ещё ни разу не печатал.
 */
export function buildFundamentalsPlan(
  snapshot: FundamentalsSnapshot,
  seed: string,
  config: LearningConfig = DEFAULT_LEARNING_CONFIG,
): FundamentalsPlan {
  const group = currentGroup(snapshot);
  const unlocked = unlockedCharacters(snapshot);
  const units: MasteryUnit[] = unlocked.map((character) => keyUnit(snapshot, character));

  return {
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    group,
    completedGroupIds: completedGroups(snapshot).map((completed) => completed.id),
    unlockedCharacters: unlocked,
    units,
    exercise: buildExercise(snapshot, seed, group, unlocked, config),
    hintLevel: FUNDAMENTALS_HINT_LEVEL,
    finished: group === null,
  };
}
